import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Copy, Check, QrCode } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const brl = (n) => (n != null ? Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "—");

export default function RechargeDialog({ open, onOpenChange, payload, onSuccess }) {
  const [step, setStep] = useState("loading");
  const [charge, setCharge] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open || !payload) return;
    setStep("loading");
    setCharge(null);
    setCopied(false);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/pix/recharge", {
          kind: payload.kind,
          amount: payload.amount ?? null,
          package_id: payload.package_id ?? null,
        });
        if (cancelled) return;
        setCharge(data);
        setStep("pay");
        startPolling(data.order_id);
      } catch (e) {
        if (cancelled) return;
        toast.error(apiError(e));
        onOpenChange(false);
      }
    })();
    return () => { cancelled = true; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payload]);

  const startPolling = (orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/pix/order/${orderId}`);
        if (data.status === "fulfilled") {
          clearInterval(pollRef.current);
          setStep("done");
          onSuccess && onSuccess();
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(charge?.brcode || "");
      setCopied(true);
      toast.success("Código Pix copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="recharge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{payload?.label || "Recarregar via Pix"}</DialogTitle>
          <DialogDescription>Pague o Pix e o valor é creditado automaticamente na sua conta.</DialogDescription>
        </DialogHeader>

        {step === "loading" && (
          <div className="flex flex-col items-center gap-3 py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Gerando Pix...</p>
          </div>
        )}

        {step === "pay" && charge && (
          <div className="flex flex-col items-center gap-4">
            {charge.brcode_base64?.startsWith("data:image") ? (
              <img src={charge.brcode_base64} alt="QR Code Pix" className="h-52 w-52 rounded-lg border border-border bg-white p-2" data-testid="recharge-qr" />
            ) : (
              <div className="flex h-52 w-52 items-center justify-center rounded-lg border border-border bg-muted"><QrCode className="h-16 w-16 text-muted-foreground" /></div>
            )}
            <div className="text-center">
              <div className="font-num text-2xl font-bold text-primary">R$ {brl(charge.amount)}</div>
              <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Aguardando pagamento...
              </p>
            </div>
            <div className="w-full space-y-2">
              <Label>Pix Copia e Cola</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={charge.brcode || ""} className="font-mono text-xs" data-testid="recharge-brcode" />
                <Button variant="outline" size="icon" onClick={copyCode} data-testid="recharge-copy-btn">
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === "done" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-4 py-6 text-center" data-testid="recharge-done">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <div>
              <h3 className="font-heading text-lg font-semibold">Recarga confirmada!</h3>
              <p className="mt-1 text-sm text-muted-foreground">O valor já está disponível na sua conta.</p>
            </div>
            <Button className="w-full" onClick={() => onOpenChange(false)} data-testid="recharge-close-btn">Fechar</Button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
